import { useSearchParams } from "react-router-dom";
import {
  Card,
  CardHeader,
  CardBody,
  MetricCard,
  InsightCard,
  DataTable,
  Chart,
  DashboardSkeleton,
  PageError,
  SampleDataBanner,
} from "../components/index.js";
import { useDashboardData } from "../hooks/useDashboardData.js";
import { rangeDays } from "../lib/time.js";

export function Conversions() {
  const { data, loading, error, refetch } = useDashboardData("conversions");
  const [searchParams] = useSearchParams();
  const days = rangeDays(searchParams.get("from"), searchParams.get("to"));

  if (loading) return <DashboardSkeleton />;
  if (error)   return <PageError error={error} onRetry={refetch} />;
  if (!data)   return null;

  const { metrics, funnel, funnelRates, formEvents, siteSearchTerms, revenueBySource, insights, period, clientContext } = data;
  const isEcommerce = clientContext?.isEcommerce;

  return (
    <div className="space-y-5 max-w-[1400px]">
      {period?.hasRealData === false && <SampleDataBanner />}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard {...metrics.onlineOrders} />
        {isEcommerce ? (
          <>
            <MetricCard {...metrics.revenue} />
            <MetricCard {...metrics.conversionRate}
              // Display conversion rate like "0.54%" — MetricCard renders
              // the number as-is, so add a % suffix via label decoration.
            />
          </>
        ) : (
          <>
            <MetricCard {...metrics.formSubmissions} />
            <MetricCard {...metrics.formStarts} />
          </>
        )}
        <MetricCard {...metrics.siteSearches} />
      </div>

      <div className={`grid grid-cols-1 gap-4 ${isEcommerce ? "" : "lg:grid-cols-3"}`}>
        <Card className={isEcommerce ? "" : "lg:col-span-2"}>
          <CardHeader
            title="E-commerce Funnel"
            subtitle="Product views → purchase · Online Store channel"
          />
          <CardBody>
            <Chart
              type="bar"
              data={funnel}
              xKey="stage"
              series={[{ key: "count", color: "#ff6804", label: "Count" }]}
              height={220}
            />
            <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-border-light">
              <FunnelRate label="View → Cart"       value={funnelRates.viewToCart} />
              <FunnelRate label="Cart → Checkout"   value={funnelRates.cartToCheckout} />
              <FunnelRate label="Checkout → Buy"    value={funnelRates.checkoutToPurchase} hint={funnelRates.checkoutToPurchaseHint} />
            </div>
          </CardBody>
        </Card>

        {/* Form Activity is hidden for e-commerce clients — their form_start
            events in GA4 are typically contact-form + search-bar interactions,
            not meaningful lead-gen conversions. Only rendered when the client
            actually runs a service business that depends on forms. */}
        {!isEcommerce && (
          <Card>
            <CardHeader title="Form Activity" subtitle="Lead gen funnel" />
            <CardBody>
              <div className="space-y-3">
                {formEvents.map((e) => {
                  const max = formEvents[0].count;
                  const pct = (e.count / max) * 100;
                  const isAbandoned = e.event === "Abandoned";
                  return (
                    <div key={e.event}>
                      <div className="flex justify-between items-baseline mb-1">
                        <span className="text-[12px] text-text-primary">{e.event}</span>
                        <span className={`text-[12px] font-medium ${isAbandoned ? "text-negative" : "text-text-primary"}`}>
                          {e.count.toLocaleString()}
                        </span>
                      </div>
                      <div className="h-1.5 bg-border-light rounded-full overflow-hidden">
                        <div
                          className={`h-full ${isAbandoned ? "bg-negative" : "bg-accent"}`}
                          style={{ width: `${pct}%`, opacity: isAbandoned ? 0.55 : 1 }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardBody>
          </Card>
        )}
      </div>

      {insights?.[0] && <InsightCard>{insights[0].content}</InsightCard>}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader title="Site Search Terms" subtitle="What visitors are looking for" />
          <DataTable
            columns={[
              { key: "term",     label: "Search Term" },
              { key: "searches", label: "Searches", align: "right" },
              { key: "insight",  label: "Opportunity" },
            ]}
            rows={siteSearchTerms}
          />
        </Card>

        <Card>
          <CardHeader title="Revenue by Source" subtitle={`Estimated attribution · ${days} days`} />
          <DataTable
            columns={[
              { key: "source",   label: "Source" },
              { key: "sessions", label: "Sessions", align: "right",
                render: (r) => r.sessions.toLocaleString() },
              { key: "revenue",  label: "Revenue", align: "right" },
              { key: "forms",    label: "Leads",   align: "right" },
            ]}
            rows={revenueBySource}
          />
        </Card>
      </div>

      {insights?.[1] && <InsightCard>{insights[1].content}</InsightCard>}
    </div>
  );
}

function FunnelRate({ label, value, hint }) {
  return (
    <div>
      <div
        className="text-[10px] uppercase text-text-muted font-medium"
        style={{ letterSpacing: "0.5px" }}
      >
        {label}
      </div>
      <div className="text-[18px] font-semibold text-text-primary mt-1">
        {value.toFixed(1)}%
      </div>
      {hint && <div className="text-[10px] text-text-muted mt-0.5">{hint}</div>}
    </div>
  );
}
